/* ODMĚNA TIPAŘI — jedna věta pro zprávu, SMS, web /d/, kino i plátno (15. 10. 2026).
 * ============================================================================
 *
 * PROČ. Majitel chce, aby tipař věděl, že dostane peníze (SPEC-STAVBA §0.3 R2).
 * Částku si zadá KAŽDÝ poradce sám v Nastavení → Doporučení (rozhodnutí hlavní
 * relace 15. 10.: jednu částku pro celý tým nedělat). Výchozí je PRÁZDNÁ — appka
 * žádné číslo nevymýšlí a texty pak jen poděkují.
 *
 * PRAVIDLA (SPEC §5.5, §6, §12):
 *   · podmínky jen dvě: „schuzka“ (za tip, ze kterého bude schůzka — výchozí)
 *     a „tip“ (za každý předaný tip). Nikdy za smlouvu, spolupráci ani objem;
 *   · bez částky VŽDY věta „Za každý tip Vám osobně poděkuji.“ — nikdy prázdno,
 *     nikdy výchozí ani odhadnuté číslo;
 *   · částka je jen kladné celé číslo 1–100 000 a nese SVOU měnu (ve které ji
 *     poradce zadal, CZ→CZK, SK→EUR). Jazyk mění jen slova a formát čísla,
 *     nikdy měnu (CLAUDE.md, lib/tenantMena.js);
 *   · ve zprávě, SMS a na webu se vyká velkým Vám, na plátně (kino, prezentace,
 *     čistý pohled) malým vám.
 *
 * Čistý modul (bez Reactu a API). React hook je components/doporuceni/
 * useOdmenaTipare.js. ⚠️ Žádné ASCII uvozovky uvnitř textů (CLAUDE.md).
 */

export const PODMINKY_ODMENY = Object.freeze(["schuzka", "tip"]);
export const VYCHOZI_PODMINKA = "schuzka";
export const MAX_ODMENA = 100000;
const MENY = Object.freeze(["CZK", "EUR"]);
const JAZYKY = Object.freeze(["cs", "sk", "en", "uk", "vi"]);
const LOCALE = Object.freeze({ cs: "cs-CZ", sk: "sk-SK", en: "en-GB", uk: "uk-UA", vi: "vi-VN" });

const jazykNebo = (jazyk) => (JAZYKY.includes(jazyk) ? jazyk : "cs");

/** Celé kladné číslo 1–100 000 (číslo nebo řetězec jen z číslic), jinak null. */
function celaCastka(v) {
  if (typeof v === "number") return Number.isInteger(v) && v >= 1 && v <= MAX_ODMENA ? v : null;
  if (typeof v !== "string" || !/^\d{1,6}$/.test(v.trim())) return null;
  const n = Number(v.trim());
  return n >= 1 && n <= MAX_ODMENA ? n : null;
}

/**
 * Vstup poradce v Nastavení → číslo k uložení.
 * Mezery (i nezlomitelné) se ignorují: „1 000“ = 1000.
 * @returns {number|null} 0 = bez odměny (prázdné pole nebo 0), 1–100 000 = částka,
 *          null = neplatné (minus, desetinné, text, víc než 100 000)
 */
export function parseCastkaOdmeny(vstup) {
  if (vstup == null) return 0;
  if (typeof vstup === "number") {
    if (vstup === 0) return 0;
    return celaCastka(vstup);
  }
  const s = String(vstup).replace(/[\s  ]/g, "");
  if (s === "" || /^0+$/.test(s)) return 0;
  return celaCastka(s);
}

/**
 * Odměna v jednom tvaru `{ castka, mena, podminka }`, nebo null (= bez částky).
 * Bere tvar z backendu (`reward: {amount, currency, condition}`), z profilu
 * poradce (`referral_reward_*`) i už hotový tvar. Cokoli rozbitého → null:
 * radši poděkovat bez čísla než slíbit číslo, které poradce nezadal.
 *
 * @param {object|null} raw
 * @param {string} [menaZalozni] měna, když u částky chybí (měna tenanta) — jen CZK/EUR
 */
export function normOdmena(raw, menaZalozni) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const castka = celaCastka(raw.castka ?? raw.amount ?? raw.referral_reward_amount);
  if (castka == null) return null;
  const menaRaw = String(raw.mena ?? raw.currency ?? raw.referral_reward_currency ?? "").trim().toUpperCase();
  const zalozni = String(menaZalozni ?? "").trim().toUpperCase();
  const mena = MENY.includes(menaRaw) ? menaRaw : (MENY.includes(zalozni) ? zalozni : "");
  if (!mena) return null;
  const podminkaRaw = raw.podminka ?? raw.condition ?? raw.referral_reward_condition;
  if (podminkaRaw != null && podminkaRaw !== "" && !PODMINKY_ODMENY.includes(podminkaRaw)) return null;
  const podminka = PODMINKY_ODMENY.includes(podminkaRaw) ? podminkaRaw : VYCHOZI_PODMINKA;
  return Object.freeze({ castka, mena, podminka });
}

/** „500 Kč“, „20 €“ — formát podle jazyka textu, měna vždy ta odměny. "" bez odměny. */
export function formatCastkyOdmeny(odmena, jazyk = "cs") {
  const o = normOdmena(odmena);
  if (!o) return "";
  try {
    return new Intl.NumberFormat(LOCALE[jazykNebo(jazyk)], {
      style: "currency", currency: o.mena, maximumFractionDigits: 0, minimumFractionDigits: 0,
    }).format(o.castka);
  } catch {
    return `${o.castka} ${o.mena}`;
  }
}

/* ── VĚTY ────────────────────────────────────────────────────────────────
 * ZPRAVA = zpráva s odkazem, SMS a stejná věta jinde v dopise (velké Vám).
 * PLATNO = řádek na plátně kina 8, pohledu B prezentace a čistého pohledu
 *          pro klienta (kratší, malé vám); dovetek jen u „schuzka“.
 * CIP    = čip pro poradce v Závěru (tyká se poradci, jako jinde v appce).
 * cs a sk doslova ze SPEC §5.5 a §5.9, en/uk/vi doplněné (vi ověří rodilý
 * mluvčí — majitel). */
const ZPRAVA = Object.freeze({
  cs: {
    schuzka: "Jako poděkování Vám za každý tip, ze kterého bude schůzka, pošlu {castka}, ať se pak rozhodne jakkoli.",
    tip: "Jako poděkování Vám za každý předaný tip pošlu {castka}.",
    bez: "Za každý tip Vám osobně poděkuji.",
  },
  sk: {
    schuzka: "Ako poďakovanie Vám za každý tip, z ktorého bude stretnutie, pošlem {castka}, nech sa potom rozhodne akokoľvek.",
    tip: "Ako poďakovanie Vám za každý odovzdaný tip pošlem {castka}.",
    bez: "Za každý tip Vám osobne poďakujem.",
  },
  en: {
    schuzka: "As a thank-you, I will send you {castka} for every tip that leads to a meeting, whatever they decide afterwards.",
    tip: "As a thank-you, I will send you {castka} for every tip you pass on.",
    bez: "I will thank you personally for every tip.",
  },
  uk: {
    schuzka: "На знак подяки я надішлю Вам {castka} за кожен контакт, з яким відбудеться зустріч, хоч би що людина потім вирішила.",
    tip: "На знак подяки я надішлю Вам {castka} за кожен переданий контакт.",
    bez: "За кожен контакт я Вам особисто подякую.",
  },
  vi: {
    schuzka: "Để cảm ơn, với mỗi người giới thiệu dẫn đến buổi gặp, tôi sẽ gửi anh/chị {castka}, dù sau đó họ quyết định thế nào.",
    tip: "Để cảm ơn, với mỗi người anh/chị giới thiệu, tôi sẽ gửi anh/chị {castka}.",
    bez: "Với mỗi người giới thiệu, tôi sẽ cảm ơn anh/chị trực tiếp.",
  },
});

const PLATNO = Object.freeze({
  cs: {
    schuzka: "Jako poděkování {castka} za každý tip, ze kterého bude schůzka.",
    tip: "Jako poděkování {castka} za každý předaný tip.",
    bez: "Za každý tip vám osobně poděkuji.",
    dovetek: "Platí, i když se nakonec pro nic nerozhodne.",
  },
  sk: {
    schuzka: "Ako poďakovanie {castka} za každý tip, z ktorého bude stretnutie.",
    tip: "Ako poďakovanie {castka} za každý odovzdaný tip.",
    bez: "Za každý tip vám osobne poďakujem.",
    dovetek: "Platí, aj keď sa nakoniec pre nič nerozhodne.",
  },
  en: {
    schuzka: "As a thank-you: {castka} for every tip that leads to a meeting.",
    tip: "As a thank-you: {castka} for every tip you pass on.",
    bez: "I will thank you personally for every tip.",
    dovetek: "This applies even if they decide against everything in the end.",
  },
  uk: {
    schuzka: "На знак подяки {castka} за кожен контакт, з яким відбудеться зустріч.",
    tip: "На знак подяки {castka} за кожен переданий контакт.",
    bez: "За кожен контакт я вам особисто подякую.",
    dovetek: "Діє, навіть якщо людина зрештою нічого не обере.",
  },
  vi: {
    schuzka: "Lời cảm ơn: {castka} cho mỗi người giới thiệu dẫn đến buổi gặp.",
    tip: "Lời cảm ơn: {castka} cho mỗi người anh/chị giới thiệu.",
    bez: "Với mỗi người giới thiệu, tôi sẽ cảm ơn anh/chị trực tiếp.",
    dovetek: "Áp dụng kể cả khi cuối cùng họ không chọn gì.",
  },
});

const CIP = Object.freeze({
  cs: { schuzka: "Poděkování: {castka} za tip se schůzkou", tip: "Poděkování: {castka} za každý tip", bez: "Poděkování bez částky", nastavit: "Nastavit" },
  sk: { schuzka: "Poďakovanie: {castka} za tip so stretnutím", tip: "Poďakovanie: {castka} za každý tip", bez: "Poďakovanie bez sumy", nastavit: "Nastaviť" },
  en: { schuzka: "Thank-you: {castka} per tip with a meeting", tip: "Thank-you: {castka} per tip", bez: "Thank-you without an amount", nastavit: "Set up" },
  uk: { schuzka: "Подяка: {castka} за контакт із зустріччю", tip: "Подяка: {castka} за кожен контакт", bez: "Подяка без суми", nastavit: "Налаштувати" },
  vi: { schuzka: "Lời cảm ơn: {castka} mỗi người có buổi gặp", tip: "Lời cảm ơn: {castka} mỗi người giới thiệu", bez: "Lời cảm ơn không kèm số tiền", nastavit: "Thiết lập" },
});

const dosadCastku = (vzor, castka) => vzor.split("{castka}").join(castka);

/**
 * Věta odměny do zprávy, SMS a dopisu (velké Vám). NIKDY prázdná:
 * bez částky (null, 0, rozbité) = „Za každý tip Vám osobně poděkuji.“
 * Tuhle funkci sdílí zpráva, SMS, web, kino i Závěr — slibují totéž.
 * @param {object|null} odmena  `{castka, mena, podminka}` (nebo tvar z backendu/profilu)
 * @param {string} jazyk        jazyk textu (cs, sk, en, uk, vi; jiný = čeština)
 */
export function vetaOdmeny(odmena, jazyk = "cs") {
  const t = ZPRAVA[jazykNebo(jazyk)];
  const o = normOdmena(odmena);
  if (!o) return t.bez;
  return dosadCastku(t[o.podminka], formatCastkyOdmeny(o, jazyk));
}

/* Ústní věta (tahák kina 8, SPEC §5.7): TÁŽ věta jako ve zprávě, jen se
 * zdvořilé „Vám“ mluví malým „vám“ (na plátně a v řeči se píše malým). */
const MALE_VAM = Object.freeze({ cs: ["Vám", "vám"], sk: ["Vám", "vám"], uk: ["Вам", "вам"] });

/**
 * Věta odměny pro tahák kina (poradce ji říká nahlas): obsah i dovětek
 * „ať se pak rozhodne jakkoli“ jako ve zprávě (vetaOdmeny), malé „vám“.
 * Bez částky = věta bez čísla.
 */
export function vetaOdmenyUstne(odmena, jazyk = "cs") {
  const j = jazykNebo(jazyk);
  const veta = vetaOdmeny(odmena, j);
  const zamena = MALE_VAM[j];
  return zamena ? veta.split(zamena[0]).join(zamena[1]) : veta;
}

/**
 * Řádek odměny na plátně, které vidí klient (kino 8, pohled B, čistý pohled):
 * `{ veta, dovetek, castka }`. Malé „vám“, částka je jen součást řádku (ne velké
 * číslo, SPEC R2). `dovetek` jen u „schuzka“, `castka` "" bez odměny.
 */
export function radekOdmeny(odmena, jazyk = "cs") {
  const t = PLATNO[jazykNebo(jazyk)];
  const o = normOdmena(odmena);
  if (!o) return { veta: t.bez, dovetek: "", castka: "" };
  const castka = formatCastkyOdmeny(o, jazyk);
  return { veta: dosadCastku(t[o.podminka], castka), dovetek: o.podminka === "schuzka" ? t.dovetek : "", castka };
}

/** Čip pro poradce (Závěr): `{ text, nastavit, sCastkou }`. */
export function cipOdmeny(odmena, jazyk = "cs") {
  const t = CIP[jazykNebo(jazyk)];
  const o = normOdmena(odmena);
  if (!o) return { text: t.bez, nastavit: t.nastavit, sCastkou: false };
  return { text: dosadCastku(t[o.podminka], formatCastkyOdmeny(o, jazyk)), nastavit: t.nastavit, sCastkou: true };
}
